import { useState } from 'react'
import { useRoles } from '../../hooks/useRoles'
import { Table } from '../../components/Common/Table'
import { Button } from '../../components/Common/Button'
import { RoleForm } from './RoleForm'

export function RolesList({ sidebarCollapsed, isDarkMode = false }) {
  const [showForm, setShowForm] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [search, setSearch] = useState('')
  const { roles, loading, deleteRole, refetch } = useRoles()

  const handleDelete = async (e, role) => {
    e.stopPropagation()
    if (!confirm(`Delete role "${role.name}"? This will remove all assignments and permissions.`)) return
    try {
      await deleteRole(role.id)
    } catch (err) {
      alert(`Failed to delete: ${err.message}`)
    }
  }

  const handleExport = async () => {
    const XLSX = await import('xlsx')
    const exportData = roles.map(r => ({
      'Role Name': r.name,
      'Description': r.description || '',
    }))
    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Roles')
    XLSX.writeFile(wb, 'Roles.xlsx')
  }

  const filteredRoles = roles.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  )

  const columns = [
    { key: 'name', label: 'Role Name' },
    { key: 'description', label: 'Description', render: (row) => row.description || '-' },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString('en-ZA')
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setEditingRole(row); setShowForm(true) }}
            className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
          >
            Edit
          </button>
          <button
            onClick={(e) => handleDelete(e, row)}
            className="text-red-500 hover:text-red-700 text-xs font-semibold px-2 py-1 rounded hover:bg-red-50 transition"
          >
            Delete
          </button>
        </div>
      )
    },
  ]

  return (
    <div className={`p-6 space-y-6 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      {/* Action Bar */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => { setEditingRole(null); setShowForm(true) }}>
            + Create Role
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            ↓ Export Excel
          </Button>
        </div>
        <input
          type="text"
          placeholder="Search roles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field max-w-xs"
        />
      </div>

      {/* Table */}
      <div className={`overflow-hidden rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <Table
          columns={columns}
          data={filteredRoles}
          loading={loading}
          onRowClick={(row) => { setEditingRole(row); setShowForm(true) }}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <RoleForm
          initialData={editingRole}
          onClose={() => { setShowForm(false); setEditingRole(null); refetch() }}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  )
}
